import { useState } from 'react';
import { Activity, BellRing, Database, FileWarning, Plus, ShieldCheck } from 'lucide-react';
import { useActiveInjuries, useArchivedInjuries, type InjuryWithPlayer } from '@/hooks/useInjuries';
import { InjuryCard } from '@/components/InjuryCard';
import { AddInjuryModal } from '@/components/AddInjuryModal';

export function IR() {
  const [adding, setAdding] = useState(false);
  const active = useActiveInjuries();
  const archived = useArchivedInjuries();

  const totalActive = active.data?.total ?? 0;
  const totalArchived = archived.data?.total ?? 0;
  const buckets = active.data?.buckets;
  const thisWeekCount = (buckets?.overdue.length ?? 0) + (buckets?.this_week.length ?? 0);
  const publicReportCount =
    active.data?.items.filter((item) => item.createdBy === 'espn-injury-report').length ?? 0;
  const verificationCount =
    active.data?.items.filter((item) => item.severity?.toLowerCase().includes('verify')).length ?? 0;
  const firstGameBack = active.data?.items.filter((item) => item.firstGameBackFlagged === 1) ?? [];

  return (
    <section className="space-y-5">
      <div className="surface overflow-hidden rounded-control">
        <div className="flex flex-col gap-5 border-b border-border/80 p-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-bartlettGold">
              Player Health Command
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-normal">IR</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted">
              Active injury watchlist, return-to-play timing, and public ESPN report matches for Bartlett clients.
            </p>
          </div>
          <button
            onClick={() => setAdding(true)}
            className="focus-ring inline-flex h-10 w-fit items-center gap-2 rounded-control border border-bartlett bg-bartlett px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-black/90"
          >
            <Plus aria-hidden="true" size={17} />
            <span>Add Injury</span>
          </button>
        </div>
        <div className="grid sm:grid-cols-4">
          <IrMetric icon={Activity} label="Active IR" value={String(totalActive)} />
          <IrMetric icon={FileWarning} label="Return watch" value={String(thisWeekCount)} tone={thisWeekCount > 0 ? 'warn' : 'default'} />
          <IrMetric icon={Database} label="Public reports" value={String(publicReportCount)} />
          <IrMetric icon={ShieldCheck} label="Verify" value={String(verificationCount)} tone={verificationCount > 0 ? 'warn' : 'default'} />
        </div>
      </div>

      <div className="flex flex-col gap-3 border-b border-border/80 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-base font-semibold tracking-normal">Active watchlist</h2>
          <p className="mt-1 text-sm text-muted">
            {totalActive} active · {totalArchived} archived (last 30 days view)
          </p>
        </div>
      </div>

      {thisWeekCount > 0 && (
        <div className="rounded-control border border-flag-warn/30 bg-flag-warn/5 px-4 py-3 text-sm shadow-sm">
          <span className="font-semibold text-flag-warn">{thisWeekCount}</span>
          <span className="text-ink"> {thisWeekCount === 1 ? 'client returning' : 'clients returning'} this week — review</span>
        </div>
      )}

      {firstGameBack.length > 0 && (
        <div className="rounded-control border border-bartlettGold/40 bg-bartlettGold/10 px-4 py-3 text-sm shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 gap-3">
              <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-bartlett" />
              <div className="min-w-0">
                <p className="font-semibold text-ink">First game back detected</p>
                <p className="mt-1 text-muted">
                  Confirm return-to-play and archive the injury record after the check-in.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {firstGameBack.slice(0, 5).map((injury) => {
                const playerName =
                  injury.player?.displayName ??
                  [injury.player?.firstName, injury.player?.lastName].filter(Boolean).join(' ') ??
                  'Unknown';
                return (
                  <a
                    key={injury.id}
                    href={`/people/${injury.playerId}?tab=injuries`}
                    className="focus-ring inline-flex items-center rounded-full border border-bartlettGold/40 bg-white px-2.5 py-1 font-mono text-[11px] text-bartlett hover:border-bartlett"
                  >
                    {playerName}
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {active.isLoading && <BucketSkeleton />}
      {active.isError && (
        <div className="rounded-control border border-flag-critical/30 bg-flag-critical/5 p-4 text-sm text-flag-critical">
          Couldn&apos;t load injuries.
        </div>
      )}

      {buckets && (
        <>
          <Bucket
            title="This week"
            subtitle="returning ≤ 7 days (or overdue)"
            items={[...buckets.overdue, ...buckets.this_week]}
            emptyText="Nobody returning this week."
          />
          <Bucket
            title="Next 30 days"
            items={buckets.next_30}
            emptyText="No projected returns in the next 30 days."
          />
          <Bucket
            title="No ETA / open"
            items={buckets.no_eta}
            emptyText="No open injuries without a target return."
          />
        </>
      )}

      {archived.data && archived.data.items.length > 0 && (
        <ArchivedSection items={archived.data.items} />
      )}

      {adding && <AddInjuryModal onClose={() => setAdding(false)} />}
    </section>
  );
}

function IrMetric({
  icon: Icon,
  label,
  value,
  tone = 'default',
}: {
  icon: typeof Activity;
  label: string;
  value: string;
  tone?: 'default' | 'warn';
}) {
  return (
    <div className="border-t border-border px-5 py-4 first:border-t-0 sm:border-l sm:border-t-0 sm:first:border-l-0">
      <div className="flex items-center gap-2 text-muted">
        <Icon size={14} />
        <span className="font-mono text-[10px] uppercase tracking-[0.14em]">{label}</span>
      </div>
      <div className={['mt-2 font-mono text-[20px] font-semibold', tone === 'warn' ? 'text-flag-warn' : 'text-ink'].join(' ')}>
        {value}
      </div>
    </div>
  );
}

function Bucket({
  title,
  subtitle,
  items,
  emptyText,
}: {
  title: string;
  subtitle?: string;
  items: InjuryWithPlayer[];
  emptyText: string;
}) {
  return (
    <div className="surface-soft space-y-2 rounded-control p-3 sm:p-4">
      <div className="flex items-baseline gap-2">
        <h2 className="text-base font-semibold">{title}</h2>
        {subtitle && (
          <span className="font-mono text-[11px] uppercase tracking-wide text-muted">{subtitle}</span>
        )}
        <span className="ml-auto font-mono text-[12px] text-muted">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="rounded-control border border-dashed border-border bg-white/60 px-3 py-2 text-[13px] italic text-muted">
          {emptyText}
        </p>
      ) : (
        <div className="overflow-hidden rounded-control border border-border bg-white shadow-sm">
          {items.map((injury) => (
            <InjuryCard key={injury.id} injury={injury} />
          ))}
        </div>
      )}
    </div>
  );
}

function ArchivedSection({ items }: { items: InjuryWithPlayer[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-baseline gap-2 text-left"
      >
        <h2 className="text-base font-semibold">Recently archived</h2>
        <span className="font-mono text-[11px] uppercase tracking-wide text-muted">
          {open ? 'hide' : 'show'} {items.length}
        </span>
      </button>
      {open && (
        <div className="overflow-hidden rounded-control border border-border bg-white">
          {items.map((injury) => (
            <InjuryCard key={injury.id} injury={injury} />
          ))}
        </div>
      )}
    </div>
  );
}

function BucketSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i}>
          <div className="mb-2 h-4 w-32 animate-pulse rounded bg-border" />
          <div className="h-12 animate-pulse rounded-control border border-border bg-white" />
        </div>
      ))}
    </div>
  );
}
